import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaUniversity,
  FaSpinner,
  FaSave,
  FaArrowLeft,
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

// Define only the fields that match the backend API for bank details
const bankFields: Array<any> = [
  {
    label: "Bank Name",
    name: "bank_name",
    required: true,
    icon: <FaUniversity className="inline mr-2 text-indigo-600" />,
  },
  { label: "Branch", name: "branch" },
  {
    label: "Account Number",
    name: "account_number",
    required: true,
  },
  { label: "IFSC Code", name: "ifsc_code" },
  { label: "MICR Code", name: "micr_code" },
  { label: "SWIFT Code", name: "swift_code" },
  { label: "Account Type", name: "account_type" },
  { label: "Account Holder Name", name: "account_holder_name" },
];

const initialBankState = {
  id: "",
  bank_name: "",
  branch: "",
  account_number: "",
  ifsc_code: "",
  micr_code: "",
  swift_code: "",
  account_type: "",
  account_holder_name: "",
  company_id: "",
};

const AddBank: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyId = searchParams.get("company_id");

  const [bank, setBank] = useState(initialBankState);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isEdit, setIsEdit] = useState(false);

  const [fetchedOnce, setFetchedOnce] = useState(false);

  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to alert on new message
    if (successMsg || errorMsg) {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [successMsg, errorMsg]);

  // Fetch bank details for companyId using specified endpoint.
  useEffect(() => {
    if (!companyId) {
      setErrorMsg("Missing company ID in URL.");
      return;
    }

    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    setFetchedOnce(false);
    axios
      .get(`${API_URL}/auth/company/${companyId}/bank`)
      .then((res) => {
        if (res.data && res.data.length > 0) {
          // only use first, if more than 1 returned
          setBank({ ...res.data[0], company_id: companyId });
          setIsEdit(true);
        } else {
          setBank({ ...initialBankState, company_id: companyId });
          setIsEdit(false);
        }
        setFetchedOnce(true);
      })
      .catch((err) => {
        setErrorMsg(
          "Unable to load bank details. Please try again later or contact support."
        );
        setBank({ ...initialBankState, company_id: companyId });
        setIsEdit(false);
        setFetchedOnce(true);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [companyId]);

  const handleBankInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setBank((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!bank.bank_name.trim() || !bank.account_number.trim()) {
      setErrorMsg("Bank name and account number are required.");
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && bank.id) {
        // Update (PUT)
        await axios.put(
          `${API_URL}/auth/company/${companyId}/bank/${bank.id}`,
          {
            ...bank,
            company_id: companyId,
          }
        );
        setSuccessMsg("Bank details updated successfully.");
      } else {
        // Create (POST)
        const res = await axios.post(
          `${API_URL}/auth/company/${companyId}/bank`,
          { ...bank, company_id: companyId }
        );
        setSuccessMsg("Bank details added successfully.");
        if (res.data && res.data.id) {
          setBank((prev) => ({ ...prev, id: res.data.id }));
          setIsEdit(true);
        }
      }
    } catch (err: any) {
      if (err.response?.data?.error) {
        setErrorMsg(err.response.data.error);
      } else {
        setErrorMsg("Failed to save bank details.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setErrorMsg("");
    setSuccessMsg("");
    setBank({ ...initialBankState, company_id: companyId ?? "" });
    setIsEdit(false);
  };

  // UI updated: inputs styled with a card input look, focus enhancements, icons, labels above, and error focus border
  return (
    <div className="min-h-[90vh] flex flex-col justify-center items-center bg-gradient-to-r from-indigo-100 via-blue-50 to-pink-50 px-2">
      <div className="w-full max-w-lg mx-auto bg-white shadow-2xl rounded-2xl p-6 md:p-10 mt-10 border border-indigo-200 relative animate__animated animate__fadeIn">
        <h2 className="text-2xl md:text-3xl font-extrabold mb-1 flex items-center gap-2 text-indigo-700 drop-shadow-sm">
          {isEdit ? "Edit Bank Details" : "Add Bank Details"}
        </h2>
        <p className="mb-5 text-gray-500 text-sm">
          {companyId
            ? "Fill and save your company's bank details. If details exist, you can update them."
            : "No company ID specified."}
        </p>
        <div ref={alertRef} className="mb-2">
          {errorMsg && <Alert type="error" message={errorMsg} />}
          {successMsg && <Alert type="success" message={successMsg} />}
        </div>
        {loading || !fetchedOnce ? (
          <div className="flex justify-center items-center text-indigo-700 font-semibold animate-pulse py-8 text-lg">
            <FaSpinner className="animate-spin mr-2" />
            Loading bank details...
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 animate__animated animate__fadeIn"
            autoComplete="off"
          >
            <div className="rounded-xl p-4 pb-2 mb-1 grid grid-cols-1 gap-4 border border-indigo-50 bg-gradient-to-tr from-white via-indigo-50 to-pink-50 shadow">
              {bankFields.map((fld: any) => (
                <FormField
                  key={fld.name}
                  label={fld.label}
                  name={fld.name}
                  required={fld.required}
                  value={bank[fld.name as keyof typeof initialBankState] as string}
                  onChange={handleBankInputChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
                  icon={fld.icon}
                  inputType="text"
                />
              ))}
            </div>
            <div className="flex items-center gap-2 justify-between pt-2 mt-2">
              <button
                type="submit"
                className="px-8 py-2 rounded-xl bg-gradient-to-br from-indigo-600 via-fuchsia-600 to-pink-500 text-white shadow-xl hover:scale-105 font-bold text-base flex items-center gap-2 transition"
                disabled={submitting}
              >
                {submitting ? (
                  <FaSpinner className="animate-spin inline" />
                ) : isEdit ? (
                  <FaSave />
                ) : (
                  <FaSave />
                )}
                {submitting
                  ? isEdit
                    ? "Updating..."
                    : "Adding..."
                  : isEdit
                  ? "Update"
                  : "Add"}
              </button>
              <button
                type="button"
                className="ml-auto px-2 py-2 rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 text-indigo-700 border border-indigo-100 hover:bg-indigo-50 font-medium shadow-sm flex items-center gap-2 transition"
                onClick={handleReset}
                disabled={submitting}
                title="Reset Form"
              >
                Clear
              </button>
              <button
                type="button"
                className="px-6 py-2 rounded-lg bg-gradient-to-br from-indigo-50 to-white text-gray-700 border border-gray-200 hover:bg-indigo-100 shadow font-medium flex items-center gap-2 transition"
                onClick={() => navigate("/company")}
                disabled={submitting}
              >
                <FaArrowLeft /> All Companies
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ALERT COMPONENT
const Alert: React.FC<{ type: "success" | "error"; message: string }> = ({
  type,
  message,
}) => {
  const icon =
    type === "success" ? (
      <FaCheckCircle className="mr-2 text-green-600" />
    ) : (
      <FaExclamationCircle className="mr-2 text-red-600" />
    );
  const color =
    type === "success"
      ? "bg-green-50 text-green-700 border-green-200"
      : "bg-red-50 text-red-700 border-red-200";
  return (
    <div
      className={`rounded border px-4 py-2 flex items-center shadow-sm font-medium ${color} animate__animated animate__fadeIn`}
    >
      {icon}
      {message}
    </div>
  );
};

// Redesigned input: label above, subtle shadow, hover/focus outline, left icon in field, error focus styles
interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  inputType?: string;
  required?: boolean;
  icon?: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  onChange,
  inputType = "text",
  required = false,
  icon = null,
}) => (
  <div className="w-full flex flex-col gap-1">
    <label
      htmlFor={name}
      className="mb-1 pl-1 text-[15px] font-semibold text-indigo-700 flex items-center"
    >
      {icon && <span className="mr-1">{icon}</span>}
      {label}
      {required && <span className="text-red-600 ml-1">*</span>}
    </label>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-400 text-lg">
          {icon}
        </span>
      )}
      <input
        type={inputType}
        name={name}
        id={name}
        autoComplete="off"
        value={value}
        onChange={onChange}
        required={required}
        className={`pl-${icon ? "10" : "4"} pr-4 py-2 w-full text-gray-800 rounded-xl bg-indigo-50 border border-indigo-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition placeholder-gray-400 font-medium`}
        placeholder={label}
      />
    </div>
  </div>
);

export default AddBank;